import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Minus, Trash, MagnifyingGlass, ChefHat, Receipt as ReceiptIcon, X, Storefront, Motorcycle } from "@phosphor-icons/react";
import ModifierModal from "@/components/ModifierModal";
import PaymentDialog from "@/components/PaymentDialog";
import ReceiptDialog from "@/components/ReceiptDialog";
import KOTDialog from "@/components/KOTDialog";
import OpenTicketsDialog from "@/components/OpenTicketsDialog";
import { resolveImageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";
import { fmt, setCurrency } from "@/lib/format";
import { printNode, buildReceiptHTML, buildKOTHTML } from "@/lib/print";
import { effectiveBasePrice, packagingPerUnit } from "@/lib/pricing";
import { useOutlet } from "@/contexts/OutletContext";

const newLineId = () =>
  (crypto?.randomUUID?.() || `l_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);

const SOURCES = [
  { key: "in_store", label: "In-store", accent: "bg-boba-matcha text-white", ring: "border-boba-matcha", tint: "text-boba-matcha", icon: Storefront },
  { key: "swiggy",   label: "Swiggy",   accent: "bg-[#FC8019] text-white",  ring: "border-[#FC8019]",  tint: "text-[#FC8019]",  icon: Motorcycle },
  { key: "zomato",   label: "Zomato",   accent: "bg-[#E23744] text-white",  ring: "border-[#E23744]",  tint: "text-[#E23744]",  icon: Motorcycle },
];

export default function POS() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [toppings, setToppings] = useState([]);
  const [settings, setSettings] = useState({ currency: "$", tax_rate: 0 });
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [openOrder, setOpenOrder] = useState(null); // {id, order_number} when working on a saved-KOT ticket
  const [kotData, setKotData] = useState(null);
  const [ticketsOpen, setTicketsOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [ticketsRefresh, setTicketsRefresh] = useState(0);
  const [source, setSource] = useState("in_store");
  const [aggregatorOrderId, setAggregatorOrderId] = useState("");
  const [confirmingAgg, setConfirmingAgg] = useState(false);

  const refreshOpenCount = async () => {
    try {
      const { data } = await api.get("/orders/open");
      setOpenCount(data.length);
    } catch (e) {
      // ignore
    }
  };

  const load = async () => {
    const [c, m, t, s] = await Promise.all([
      api.get("/categories"),
      api.get("/menu-items"),
      api.get("/toppings"),
      api.get("/settings"),
    ]);
    setCategories(c.data);
    setMenuItems(m.data);
    setToppings(t.data);
    setSettings(s.data);
    setCurrency(s.data.currency);
  };

  useEffect(() => {
    load();
    refreshOpenCount();
  }, []);

  const filtered = useMemo(() => {
    return menuItems.filter((m) => {
      if (!m.available) return false;
      if (activeCategory !== "all" && m.category_id !== activeCategory) return false;
      if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [menuItems, activeCategory, search]);

  const openModifier = (item, idx = null) => {
    // Override base_price so the modal computes correct pricing for the active source
    const eff = effectiveBasePrice(item, source, settings);
    setEditingItem({ ...item, base_price: eff, _originalBasePrice: item.base_price });
    setEditingIndex(idx);
  };

  const handleAddToCart = (cartLine) => {
    if (editingIndex !== null) {
      const next = [...cart];
      // preserve line_id and kot_seq if this is an already-sent line
      next[editingIndex] = { ...cartLine, line_id: next[editingIndex].line_id, kot_seq: next[editingIndex].kot_seq || 0 };
      setCart(next);
    } else {
      setCart([...cart, { ...cartLine, line_id: newLineId(), kot_seq: 0 }]);
    }
    setEditingItem(null);
    setEditingIndex(null);
  };

  const startNewOrder = () => {
    if (cart.length > 0 && !window.confirm("Discard current cart and start a new order?")) return;
    setCart([]);
    setOpenOrder(null);
  };

  const loadOpenTicket = (order) => {
    setCart(order.items.map((it) => ({ ...it })));
    setOpenOrder({ id: order.id, order_number: order.order_number });
    toast.success(`Loaded ticket #${order.order_number}`);
  };

  const saveKOT = async () => {
    const pending = cart.filter((c) => !c.kot_seq);
    if (pending.length === 0) {
      toast.info("No new items to send to kitchen");
      return;
    }
    try {
      const { data } = await api.post("/orders/kot", {
        order_id: openOrder?.id || null,
        items: cart,
      });
      setOpenOrder({ id: data.order.id, order_number: data.order.order_number });
      setCart(data.order.items.map((it) => ({ ...it })));
      setKotData(data);
      setTicketsRefresh((x) => x + 1);
      refreshOpenCount();
      toast.success(`KOT #${data.kot_batch.kot_number} sent to kitchen`);
      if (settings.auto_print_kot && data.kot_batch) {
        try { printNode(buildKOTHTML(data, settings), `KOT #${data.kot_batch.kot_number}`); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save KOT");
    }
  };

  const updateQty = (idx, delta) => {
    const next = [...cart];
    const line = next[idx];
    if (line.kot_seq) {
      toast.error("Items already sent to kitchen can't be changed. Remove & re-add if needed.");
      return;
    }
    const newQty = line.quantity + delta;
    if (newQty <= 0) {
      next.splice(idx, 1);
    } else {
      const it = { ...line, quantity: newQty };
      it.line_total = +(it.unit_price * newQty).toFixed(2);
      next[idx] = it;
    }
    setCart(next);
  };

  const removeLine = (idx) => {
    if (cart[idx]?.kot_seq) {
      toast.error("Already sent to kitchen — discard the whole ticket instead.");
      return;
    }
    const next = [...cart];
    next.splice(idx, 1);
    setCart(next);
  };

  const editLine = (idx) => {
    if (cart[idx]?.kot_seq) {
      toast.error("Item already sent to kitchen.");
      return;
    }
    const line = cart[idx];
    const item = menuItems.find((m) => m.id === line.menu_item_id);
    if (item) openModifier({ ...item, _editing: line }, idx);
  };

  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.line_total, 0), [cart]);
  const { activeOutlet } = useOutlet();
  const effectiveTaxRate = (activeOutlet?.tax_rate ?? settings.tax_rate) || 0;
  const tax = +(subtotal * effectiveTaxRate).toFixed(2);
  const packagingTotal = useMemo(() => {
    if (source === "in_store") return 0;
    return +cart.reduce((s, c) => {
      const mi = menuItems.find((m) => m.id === c.menu_item_id);
      return s + packagingPerUnit(mi, source) * c.quantity;
    }, 0).toFixed(2);
  }, [cart, source, menuItems]);
  const total = +(subtotal + tax + packagingTotal).toFixed(2);

  const submitOrder = async ({ payment_method, cash_received }) => {
    const payload = {
      items: cart,
      payment_method,
      cash_received: payment_method === "cash" ? cash_received : null,
      discount: 0,
      source,
      aggregator_order_id: source !== "in_store" ? (aggregatorOrderId || null) : null,
    };
    try {
      let data;
      if (openOrder?.id) {
        // settle the open ticket
        const res = await api.post(`/orders/${openOrder.id}/settle`, payload);
        data = res.data;
      } else {
        // quick mode
        const res = await api.post("/orders", payload);
        data = res.data;
      }
      setReceipt(data);
      setCart([]);
      setOpenOrder(null);
      setPayOpen(false);
      setSource("in_store");
      setAggregatorOrderId("");
      refreshOpenCount();
      toast.success(`Order #${data.order_number} completed`);
      load();
      if (settings.auto_print_kot && data.kot_batches?.length) {
        const lastBatch = data.kot_batches[data.kot_batches.length - 1];
        try { printNode(buildKOTHTML({ order: data, kot_batch: lastBatch, kot_items: data.items }, settings), `KOT #${lastBatch.kot_number}`); } catch (e) { /* ignore */ }
      }
      if (settings.auto_print_receipt) {
        try { printNode(buildReceiptHTML(data, settings), `Receipt #${data.order_number}`); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    }
  };

  const confirmAggregatorOrder = async () => {
    if (cart.length === 0) return;
    if (!aggregatorOrderId.trim()) {
      toast.error(`Enter the ${source === "swiggy" ? "Swiggy" : "Zomato"} order #`);
      return;
    }
    setConfirmingAgg(true);
    await submitOrder({ payment_method: "aggregator", cash_received: null });
    setConfirmingAgg(false);
  };

  const discardCurrentTicket = async () => {
    if (!openOrder?.id) return;
    if (!window.confirm("Discard this open ticket? Items already sent to kitchen will be voided.")) return;
    try {
      await api.delete(`/orders/${openOrder.id}/open`);
      setCart([]);
      setOpenOrder(null);
      refreshOpenCount();
      toast.success("Ticket discarded");
    } catch (e) {
      toast.error("Failed to discard");
    }
  };

  const pendingCount = cart.filter((c) => !c.kot_seq).length;

  return (
    <div className="h-screen flex bg-boba-cream">
      {/* Categories rail */}
      <div className="w-44 bg-white border-r border-boba-stoneLine p-3 overflow-y-auto scroll-thin" data-testid="categories-rail">
        <button
          onClick={() => setActiveCategory("all")}
          className={`w-full text-left px-3 py-3 rounded-md mb-1 text-sm font-medium transition-colors ${
            activeCategory === "all"
              ? "bg-boba-matcha text-white"
              : "text-stone-700 hover:bg-stone-100"
          }`}
          data-testid="category-all"
        >
          <span className="mr-2">🍵</span> All
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCategory(c.id)}
            className={`w-full text-left px-3 py-3 rounded-md mb-1 text-sm font-medium transition-colors ${
              activeCategory === c.id
                ? "bg-boba-matcha text-white"
                : "text-stone-700 hover:bg-stone-100"
            }`}
            data-testid={`category-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="mr-2">{c.emoji}</span> {c.name}
          </button>
        ))}
      </div>

      {/* Products grid */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="p-5 border-b border-boba-stoneLine bg-white flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold leading-none">Menu</h2>
            <p className="text-xs text-boba-inkSoft mt-1 font-mono uppercase tracking-widest">Tap an item to customize</p>
          </div>
          <div className="relative w-72">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search drinks…"
              className="w-full h-11 pl-10 pr-3 border border-boba-stoneLine rounded-md bg-boba-cream focus:outline-none focus:border-boba-matcha"
              data-testid="menu-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="menu-grid">
            {filtered.map((item) => (
              <div
                key={item.id}
                onClick={() => openModifier(item)}
                className="product-card"
                data-testid={`menu-item-${item.id}`}
              >
                <div className="aspect-[4/3] bg-boba-muted overflow-hidden">
                  {item.image_url ? (
                    <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">🥤</div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-semibold leading-tight text-boba-ink text-sm">{item.name}</div>
                  <div className="text-[11px] text-boba-inkSoft mt-1 line-clamp-1">{item.description}</div>
                  {source === "in_store" ? (
                    <div className="mt-2 font-mono font-semibold text-boba-matcha">{fmt(item.base_price)}</div>
                  ) : (() => {
                    const eff = effectiveBasePrice(item, source, settings);
                    const pack = packagingPerUnit(item, source);
                    const changed = eff !== item.base_price;
                    const color = source === "swiggy" ? "text-[#B85A0F]" : "text-[#A62432]";
                    return (
                      <div className="mt-2">
                        <div className={`font-mono font-semibold ${color}`}>{fmt(eff)}</div>
                        <div className="text-[10px] text-boba-inkSoft mt-0.5 font-mono flex items-center gap-1">
                          {changed && <span className="line-through">{fmt(item.base_price)}</span>}
                          {pack > 0 && <span>+ pkg {fmt(pack)}</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-boba-inkSoft py-12">No items found</div>
            )}
          </div>
        </div>
      </div>

      {/* Cart */}
      <div className="w-[380px] bg-white border-l border-boba-stoneLine flex flex-col" data-testid="cart-panel">
        <div className="p-5 border-b border-boba-stoneLine">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-bold leading-none">Order Ticket</h2>
              <p className="text-xs text-boba-inkSoft mt-1 font-mono uppercase tracking-widest">
                {cart.length} item{cart.length !== 1 ? "s" : ""}
                {openOrder && <span className="text-boba-matcha"> · Open #{openOrder.order_number}</span>}
              </p>
            </div>
            <button
              onClick={() => setTicketsOpen(true)}
              className="relative h-9 px-3 border border-boba-stoneLine rounded-md text-xs font-medium hover:bg-stone-50 flex items-center gap-1.5"
              data-testid="open-tickets-btn"
            >
              <ReceiptIcon size={14} weight="duotone" /> Tickets
              {openCount > 0 && (
                <span className="ml-1 bg-boba-matcha text-white rounded-full px-1.5 text-[10px] font-mono font-bold">
                  {openCount}
                </span>
              )}
            </button>
          </div>
          {openOrder && (
            <button
              onClick={discardCurrentTicket}
              className="mt-2 text-[11px] text-boba-danger hover:underline flex items-center gap-1"
              data-testid="discard-current-ticket"
            >
              <X size={12} /> Discard ticket
            </button>
          )}

          {/* Source selector */}
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono mb-1.5">Order source</div>
            <div className="grid grid-cols-3 gap-1.5" data-testid="source-selector">
              {SOURCES.map((s) => {
                const Icon = s.icon;
                const active = source === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => {
                      if (openOrder && s.key !== "in_store") {
                        toast.error("Aggregator orders can't be created from an open in-store ticket");
                        return;
                      }
                      if (s.key === source) return;
                      // Reprice pending cart lines when source changes
                      if (cart.some((c) => !c.kot_seq)) {
                        const nextCart = cart.map((line) => {
                          if (line.kot_seq) return line;  // don't touch already-sent lines
                          const mi = menuItems.find((m) => m.id === line.menu_item_id);
                          if (!mi) return line;
                          const newBase = effectiveBasePrice(mi, s.key, settings);
                          const sizeDelta = (mi.sizes || []).find((sz) => sz.name === line.size)?.price_delta || 0;
                          const toppingsSum = (line.toppings || []).reduce((sum, t) => sum + (t.price || 0), 0);
                          const newUnit = +(newBase + sizeDelta + toppingsSum).toFixed(2);
                          return { ...line, unit_price: newUnit, line_total: +(newUnit * line.quantity).toFixed(2) };
                        });
                        setCart(nextCart);
                      }
                      setSource(s.key);
                      if (s.key === "in_store") setAggregatorOrderId("");
                    }}
                    disabled={!!openOrder && s.key !== "in_store"}
                    className={`h-10 rounded-md text-xs font-semibold border transition-colors flex items-center justify-center gap-1 ${active ? s.accent + " border-transparent" : `bg-white ${s.tint} border-boba-stoneLine hover:bg-stone-50`} disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`source-${s.key}`}
                  >
                    <Icon size={14} weight={active ? "fill" : "duotone"} /> {s.label}
                  </button>
                );
              })}
            </div>
            {source !== "in_store" && (
              <div className="mt-2">
                <input
                  value={aggregatorOrderId}
                  onChange={(e) => setAggregatorOrderId(e.target.value)}
                  placeholder={`${source === "swiggy" ? "Swiggy" : "Zomato"} order #`}
                  className="w-full h-9 px-3 border border-boba-stoneLine rounded-md text-sm font-mono"
                  data-testid="aggregator-order-id"
                />
                <div className="text-[10px] text-boba-inkSoft mt-1 font-mono">
                  Aggregator handles payment · commission auto-applied from Settings
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin p-4 space-y-3" data-testid="cart-items">
          {cart.length === 0 && (
            <div className="text-center text-boba-inkSoft py-12">
              <div className="text-5xl mb-3">🧋</div>
              <div className="text-sm">Cart is empty</div>
              <div className="text-xs mt-1">Tap items to add them</div>
            </div>
          )}
          {cart.map((line, idx) => (
            <div key={line.line_id || idx} className={`border rounded-md p-3 ${line.kot_seq ? "border-boba-matcha/30 bg-boba-matcha/5" : "border-boba-stoneLine"}`} data-testid={`cart-line-${idx}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <button onClick={() => editLine(idx)} className="text-left">
                    <div className="font-semibold text-sm text-boba-ink">{line.name}</div>
                  </button>
                  <div className="text-[11px] text-boba-inkSoft mt-0.5">
                    {line.size} · Sugar {line.sugar} · Ice {line.ice}
                  </div>
                  {line.toppings.length > 0 && (
                    <div className="text-[11px] text-boba-inkSoft mt-0.5">
                      + {line.toppings.map((t) => t.name).join(", ")}
                    </div>
                  )}
                  {line.kot_seq > 0 && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-boba-matcha bg-white px-1.5 py-0.5 rounded">
                      <ChefHat size={10} weight="fill" /> Sent · KOT {line.kot_seq}
                    </div>
                  )}
                </div>
                <div className="font-mono font-semibold text-boba-matcha text-sm">{fmt(line.line_total)}</div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => updateQty(idx, -1)}
                    className="w-7 h-7 border border-stone-300 rounded-md flex items-center justify-center hover:bg-stone-100"
                    data-testid={`cart-decrease-${idx}`}
                  >
                    <Minus size={14} />
                  </button>
                  <span className="font-mono font-semibold w-8 text-center" data-testid={`cart-qty-${idx}`}>{line.quantity}</span>
                  <button
                    onClick={() => updateQty(idx, 1)}
                    className="w-7 h-7 border border-stone-300 rounded-md flex items-center justify-center hover:bg-stone-100"
                    data-testid={`cart-increase-${idx}`}
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  onClick={() => removeLine(idx)}
                  className="text-boba-danger hover:bg-red-50 p-1.5 rounded"
                  data-testid={`cart-remove-${idx}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-boba-stoneLine p-5 bg-boba-cream">
          <div className="space-y-1.5 text-sm font-mono">
            <div className="flex justify-between text-boba-inkSoft">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
            </div>
            {packagingTotal > 0 && (
              <div className="flex justify-between text-boba-inkSoft" data-testid="cart-packaging-row">
                <span>Packaging</span>
                <span data-testid="cart-packaging">{fmt(packagingTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-boba-inkSoft">
              <span>Tax ({Math.round(effectiveTaxRate * 100)}%)</span>
              <span>{fmt(tax)}</span>
            </div>
            <div className="flex justify-between text-lg font-semibold text-boba-ink pt-2 border-t border-boba-stoneLine mt-2">
              <span className="font-sans">Total</span>
              <span data-testid="cart-total">{fmt(total)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-5 mb-12">
            {source === "in_store" ? (
              <>
                <button
                  onClick={saveKOT}
                  disabled={pendingCount === 0}
                  className="h-14 border-2 border-boba-matcha text-boba-matcha hover:bg-boba-matcha/5 rounded-md font-semibold text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="save-kot-button"
                >
                  <ChefHat size={20} weight="duotone" /> Save KOT
                  {pendingCount > 0 && (
                    <span className="bg-boba-matcha text-white rounded-full px-1.5 text-[10px] font-mono font-bold">
                      {pendingCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setPayOpen(true)}
                  disabled={cart.length === 0}
                  className="h-14 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-semibold text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pay-button"
                >
                  {openOrder ? "Settle" : "Charge"} {fmt(total)}
                </button>
              </>
            ) : (
              <button
                onClick={confirmAggregatorOrder}
                disabled={cart.length === 0 || !aggregatorOrderId.trim() || confirmingAgg}
                className={`col-span-2 h-14 rounded-md font-semibold text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white ${source === "swiggy" ? "bg-[#FC8019] hover:bg-[#E5730B]" : "bg-[#E23744] hover:bg-[#C82737]"}`}
                data-testid="confirm-aggregator-btn"
              >
                <ChefHat size={20} weight="duotone" />
                Send to Kitchen · {fmt(total)}
              </button>
            )}
          </div>
        </div>
      </div>

      {editingItem && (
        <ModifierModal
          item={editingItem}
          toppings={toppings}
          onClose={() => {
            setEditingItem(null);
            setEditingIndex(null);
          }}
          onConfirm={handleAddToCart}
        />
      )}

      <PaymentDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={total}
        onConfirm={submitOrder}
      />

      <ReceiptDialog order={receipt} settings={settings} onClose={() => setReceipt(null)} />

      <KOTDialog data={kotData} settings={settings} onClose={() => setKotData(null)} />

      <OpenTicketsDialog
        open={ticketsOpen}
        onClose={() => setTicketsOpen(false)}
        onSelect={loadOpenTicket}
        refreshKey={ticketsRefresh}
      />
    </div>
  );
}
